import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { authApi } from '../api';
import { TOKEN_STORAGE_KEY } from '../api/axiosClient';

const AUTH_USER_STORAGE_KEY = 'menu_auth_user';
const AUTH_FEATURES_STORAGE_KEY = 'menu_auth_features';

const SELECTED_ESTABLISHMENT_STORAGE_KEY =
  'selected_establishment_id';
const SELECTED_MENU_STORAGE_KEY = 'selected_menu_id';
const SELECTED_MENU_ESTABLISHMENT_STORAGE_KEY =
  'selected_menu_establishment_id';

const AuthContext = createContext(null);

const getStoredUser = () => {
  try {
    const storedUser = localStorage.getItem(AUTH_USER_STORAGE_KEY);

    return storedUser ? JSON.parse(storedUser) : null;
  } catch {
    return null;
  }
};

const getStoredFeatures = () => {
  try {
    const storedFeatures = localStorage.getItem(AUTH_FEATURES_STORAGE_KEY);

    return storedFeatures ? JSON.parse(storedFeatures) : {};
  } catch {
    return {};
  }
};

const clearSelectedMenuState = () => {
  localStorage.removeItem(SELECTED_ESTABLISHMENT_STORAGE_KEY);
  localStorage.removeItem(SELECTED_MENU_STORAGE_KEY);
  localStorage.removeItem(SELECTED_MENU_ESTABLISHMENT_STORAGE_KEY);
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getStoredUser);
  const [features, setFeatures] = useState(getStoredFeatures);
  const [initializing, setInitializing] = useState(true);

  const token = localStorage.getItem(TOKEN_STORAGE_KEY);

  const isAuthenticated = Boolean(token && user);

  const saveUser = useCallback((nextUser) => {
    if (!nextUser) {
      localStorage.removeItem(AUTH_USER_STORAGE_KEY);
      setUser(null);
      return;
    }

    localStorage.setItem(
      AUTH_USER_STORAGE_KEY,
      JSON.stringify(nextUser),
    );

    setUser(nextUser);
  }, []);

  const saveFeatures = useCallback((nextFeatures) => {
    const normalizedFeatures =
      nextFeatures &&
      typeof nextFeatures === 'object' &&
      !Array.isArray(nextFeatures)
        ? nextFeatures
        : {};

    localStorage.setItem(
      AUTH_FEATURES_STORAGE_KEY,
      JSON.stringify(normalizedFeatures),
    );

    setFeatures(normalizedFeatures);
  }, []);

  const saveAuthData = useCallback(
    (authData) => {
      const nextUser = authData?.user || null;
      const nextToken = authData?.token || null;
      const nextFeatures = authData?.features || {};

      if (nextToken) {
        localStorage.setItem(TOKEN_STORAGE_KEY, nextToken);
      }

      if (nextUser?.establishment_id) {
        localStorage.setItem(
          SELECTED_ESTABLISHMENT_STORAGE_KEY,
          String(nextUser.establishment_id),
        );
      } else {
        localStorage.removeItem(
          SELECTED_ESTABLISHMENT_STORAGE_KEY,
        );
      }

      saveUser(nextUser);
      saveFeatures(nextFeatures);
    },
    [saveFeatures, saveUser],
  );

  const clearAuthData = useCallback(() => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(AUTH_USER_STORAGE_KEY);
    localStorage.removeItem(AUTH_FEATURES_STORAGE_KEY);

    clearSelectedMenuState();

    setUser(null);
    setFeatures({});
  }, []);

  const login = useCallback(
    async (payload) => {
      const response = await authApi.login(payload);

      // A menu id saved before migrate:fresh or before switching users can point
      // to a menu that no longer exists. Clear it before the dashboard loads.
      clearSelectedMenuState();

      const authData = response.data.data || {};

      saveAuthData(authData);

      return authData;
    },
    [saveAuthData],
  );

  const refreshUser = useCallback(async () => {
    const currentToken = localStorage.getItem(TOKEN_STORAGE_KEY);

    if (!currentToken) {
      clearAuthData();
      setInitializing(false);

      return null;
    }

    try {
      const response = await authApi.me();

      const responseData = response.data.data || {};
      const nextUser = responseData.user || null;
      const nextFeatures = responseData.features || {};

      if (!nextUser) {
        clearAuthData();
        return null;
      }

      if (nextUser?.establishment_id) {
        localStorage.setItem(
          SELECTED_ESTABLISHMENT_STORAGE_KEY,
          String(nextUser.establishment_id),
        );
      } else {
        localStorage.removeItem(
          SELECTED_ESTABLISHMENT_STORAGE_KEY,
        );
      }

      saveUser(nextUser);
      saveFeatures(nextFeatures);

      return nextUser;
    } catch {
      clearAuthData();

      return null;
    } finally {
      setInitializing(false);
    }
  }, [
    clearAuthData,
    saveFeatures,
    saveUser,
  ]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Token may already be expired.
    } finally {
      clearAuthData();
    }
  }, [clearAuthData]);

  const getFeature = useCallback(
    (featureKey) => {
      if (!featureKey) {
        return null;
      }

      return features?.[featureKey] || null;
    },
    [features],
  );

  const isFeatureVisible = useCallback(
    (featureKey) => {
      const feature = getFeature(featureKey);

      return feature?.visible === true;
    },
    [getFeature],
  );

  const isFeatureAvailable = useCallback(
    (featureKey) => {
      const feature = getFeature(featureKey);

      return (
        feature?.visible === true &&
        feature?.available === true &&
        feature?.locked !== true
      );
    },
    [getFeature],
  );

  const isFeatureLocked = useCallback(
    (featureKey) => {
      const feature = getFeature(featureKey);

      return (
        feature?.visible === true &&
        (
          feature?.locked === true ||
          feature?.available === false
        )
      );
    },
    [getFeature],
  );

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const value = useMemo(
    () => ({
      user,
      features,
      initializing,
      isAuthenticated,

      login,
      logout,
      refreshUser,
      clearAuthData,

      getFeature,
      isFeatureVisible,
      isFeatureAvailable,
      isFeatureLocked,
    }),
    [
      user,
      features,
      initializing,
      isAuthenticated,
      login,
      logout,
      refreshUser,
      clearAuthData,
      getFeature,
      isFeatureVisible,
      isFeatureAvailable,
      isFeatureLocked,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error(
      'useAuth must be used inside AuthProvider',
    );
  }

  return context;
};

export default AuthContext;